import { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Package, ShoppingBag, CalendarClock, LayoutDashboard, Plus, Pencil, Trash2, DollarSign, LogOut, Mail, Send, CheckCircle2, Truck, ShoppingBasket, Ticket, FileText, Users, UserRound, Search, Clock, Gift, Star, Sparkles } from "lucide-react";
import { api, HERO_BG, resolveUrl, API } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const empty = { name: "", slug: "", price: 0, stock: 0, size: "", label_name: "", category: "General", purity: "≥ 99%", short: "", description: "", benefits: "", coa_url: "", best_seller: false, image: "" };
const emptyShip = { name: "", carrier: "USPS", price: 0, description: "", free_over: "", enabled: true, sort_order: 0 };

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [carts, setCarts] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [shipMethods, setShipMethods] = useState([]);
  const [shipMethodDialog, setShipMethodDialog] = useState({ open: false, editing: null, form: emptyShip });
  const [users, setUsers] = useState([]);
  const [userDialog, setUserDialog] = useState({ open: false, name: "", email: "", password: "" });
  const [clientSearch, setClientSearch] = useState("");
  const [promoDialog, setPromoDialog] = useState({ open: false, client: null, percent: 10, note: "", sending: false });
  const [sessionTimeout, setSessionTimeout] = useState(30);
  const [savingTimeout, setSavingTimeout] = useState(false);
  const [rewardsCfg, setRewardsCfg] = useState({ enabled: true, earn_spend: 100, earn_reward: 5, min_redeem: 5, expiry_months: 0 });
  const [savingRewards, setSavingRewards] = useState(false);
  const [upsellCfg, setUpsellCfg] = useState({ enabled: false, product_slugs: [] });
  const [savingUpsell, setSavingUpsell] = useState(false);
  const [saleCfg, setSaleCfg] = useState({ enabled: false, name: "", emoji: "", message: "", type: "percent", value: 0, allow_discount_codes: true, code_combine: "best", start_at: "", end_at: "" });
  const [savingSale, setSavingSale] = useState(false);
  const [pointsDialog, setPointsDialog] = useState({ open: false, client: null, amount: 0, note: "", saving: false });
  const [bookingDoc, setBookingDoc] = useState({ open: false, booking: null, amount: "", description: "", scheduled_at: "", duration_min: 30 });
  const [discForm, setDiscForm] = useState({ code: "", type: "percent", value: 10 });
  const [winbackDialog, setWinbackDialog] = useState({ open: false, cart: null, percent: 10, sending: false });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploadingCoa, setUploadingCoa] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [emailDialog, setEmailDialog] = useState({ open: false, booking: null, subject: "", message: "", sending: false });
  const [shipDialog, setShipDialog] = useState({ open: false, order: null, tracking: "", carrier: "", sending: false });

  const uploadCoa = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCoa(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/coa/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, coa_url: data.url }));
      toast.success("COA uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingCoa(false);
      e.target.value = "";
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [s, p, o, b, cc, dd, sm, uu, ss, rw, up, sl] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/products"),
        api.get("/admin/orders"),
        api.get("/admin/bookings"),
        api.get("/admin/abandoned-carts"),
        api.get("/admin/discounts"),
        api.get("/admin/shipping-methods"),
        api.get("/admin/users"),
        api.get("/settings/session"),
        api.get("/settings/rewards"),
        api.get("/settings/upsell"),
        api.get("/admin/settings/sale"),
      ]);
      setStats(s.data);
      setProducts(p.data);
      setOrders(o.data);
      setBookings(b.data);
      setCarts(cc.data);
      setDiscounts(dd.data);
      setShipMethods(sm.data);
      setUsers(uu.data);
      setSessionTimeout(Number(ss.data.timeout_minutes) || 0);
      setRewardsCfg({
        enabled: !!rw.data.enabled,
        earn_spend: Number(rw.data.earn_spend),
        earn_reward: Number(rw.data.earn_reward),
        min_redeem: Number(rw.data.min_redeem),
        expiry_months: Number(rw.data.expiry_months),
      });
      setUpsellCfg({ enabled: !!up.data.enabled, product_slugs: up.data.product_slugs || [] });
      setSaleCfg({
        enabled: !!sl.data.enabled,
        name: sl.data.name || "",
        emoji: sl.data.emoji || "",
        message: sl.data.message || "",
        type: sl.data.type || "percent",
        value: Number(sl.data.value) || 0,
        allow_discount_codes: sl.data.allow_discount_codes !== false,
        code_combine: sl.data.code_combine || "best",
        start_at: sl.data.start_at || "",
        end_at: sl.data.end_at || "",
      });
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access required");
    }
  }, []);

  useEffect(() => {
    if (user && user.is_admin) loadAll();
  }, [user, loadAll]);

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, benefits: (p.benefits || []).join("\n") }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name || !form.slug || !form.price) { toast.error("Name, slug and price are required"); return; }
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock, 10) || 0, benefits: (form.benefits || "").split("\n").map((s) => s.trim()).filter(Boolean) };
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      loadAll();
    } catch { toast.error("Delete failed"); }
  };

  const updateBooking = async (id, status) => {
    try { await api.patch(`/admin/bookings/${id}?status=${status}`); toast.success("Booking updated"); loadAll(); }
    catch { toast.error("Update failed"); }
  };

  const noteVal = (item) => (noteDrafts[item.id] !== undefined ? noteDrafts[item.id] : (item.notes || ""));
  const setNote = (id) => (e) => setNoteDrafts((d) => ({ ...d, [id]: e.target.value }));

  const saveOrderNotes = async (id) => {
    try { await api.patch(`/admin/orders/${id}/notes`, { notes: noteDrafts[id] ?? "" }); toast.success("Notes saved"); loadAll(); }
    catch { toast.error("Could not save notes"); }
  };
  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete order for ${o.email || "guest"}?`)) return;
    try { await api.delete(`/admin/orders/${o.id}`); toast.success("Order deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };
  const emailOrder = async (o) => {
    try { await api.post(`/admin/orders/${o.id}/resend-confirmation`); toast.success(`Confirmation + receipt re-sent to ${o.email}`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
  };
  const resendBookingConfirmation = async (b) => {
    try { await api.post(`/admin/bookings/${b.id}/resend-confirmation`); toast.success(`Confirmation + calendar invite re-sent to ${b.email}`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
  };
  const nudgeBooking = async (b) => {
    try { await api.post(`/admin/bookings/${b.id}/nudge`); toast.success(`Booking reminder sent to ${b.email}`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
  };

  const markPaid = async (o) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/mark-paid`);
      toast.success(data.emailed ? `Marked paid · receipt emailed to ${o.email}` : "Marked paid (email pending domain verification)");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const openShip = (o) => setShipDialog({ open: true, order: o, tracking: o.tracking_number || "", carrier: o.carrier || "", sending: false });
  const submitShip = async () => {
    const { order, tracking, carrier } = shipDialog;
    if (!tracking) { toast.error("Tracking number is required"); return; }
    setShipDialog((d) => ({ ...d, sending: true }));
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/ship`, { tracking_number: tracking, carrier });
      toast.success(data.emailed ? `Marked shipped · tracking emailed to ${order.email}` : "Marked shipped (email pending domain verification)");
      setShipDialog({ open: false, order: null, tracking: "", carrier: "", sending: false });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      setShipDialog((d) => ({ ...d, sending: false }));
    }
  };

  const saveBookingNotes = async (id) => {
    try { await api.patch(`/admin/bookings/${id}/notes`, { notes: noteDrafts[id] ?? "" }); toast.success("Notes saved"); loadAll(); }
    catch { toast.error("Could not save notes"); }
  };
  const deleteBooking = async (b) => {
    if (!window.confirm(`Delete booking for ${b.name}?`)) return;
    try { await api.delete(`/admin/bookings/${b.id}`); toast.success("Booking deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  const remindCart = async (c) => {
    try { await api.post(`/admin/abandoned-carts/${c.id}/remind`); toast.success(`Reminder sent to ${c.email}`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send reminder"); }
  };
  const sendAllReminders = async () => {
    try { const { data } = await api.post(`/admin/abandoned-carts/send-reminders`); toast.success(`${data.sent} reminder(s) sent`); loadAll(); }
    catch { toast.error("Could not send reminders"); }
  };
  const openWinback = (c) => setWinbackDialog({ open: true, cart: c, percent: 10, sending: false });
  const submitWinback = async () => {
    const { cart, percent } = winbackDialog;
    setWinbackDialog((d) => ({ ...d, sending: true }));
    try {
      const { data } = await api.post(`/admin/abandoned-carts/${cart.id}/win-back`, { percent: Number(percent) });
      toast.success(`Discount ${data.code} sent to ${cart.email}`);
      setWinbackDialog({ open: false, cart: null, percent: 10, sending: false });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send discount");
      setWinbackDialog((d) => ({ ...d, sending: false }));
    }
  };
  const createDiscount = async () => {
    if (!discForm.code || !discForm.value) { toast.error("Code and value are required"); return; }
    try {
      await api.post("/admin/discounts", { code: discForm.code, type: discForm.type, value: Number(discForm.value) });
      toast.success("Discount created");
      setDiscForm({ code: "", type: "percent", value: 10 });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create discount"); }
  };
  const deleteDiscount = async (d) => {
    if (!window.confirm(`Delete code ${d.code}?`)) return;
    try { await api.delete(`/admin/discounts/${d.id}`); toast.success("Discount deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };
  const cartSubtotal = (c) => (c.items || []).reduce((s, i) => s + i.price * i.quantity, 0);
  const deleteCart = async (c) => {
    if (!window.confirm(`Delete abandoned cart for ${c.email}?`)) return;
    try { await api.delete(`/admin/abandoned-carts/${c.id}`); toast.success("Cart deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  const openShipMethod = (m) => setShipMethodDialog({ open: true, editing: m, form: m ? { ...emptyShip, ...m, free_over: m.free_over ?? "" } : emptyShip });
  const setShipField = (k) => (e) => setShipMethodDialog((d) => ({ ...d, form: { ...d.form, [k]: e.target.value } }));
  const saveShipMethod = async () => {
    const f = shipMethodDialog.form;
    if (!f.name) { toast.error("Name is required"); return; }
    const payload = {
      name: f.name, carrier: f.carrier || "", price: Number(f.price) || 0,
      description: f.description || "", enabled: !!f.enabled, sort_order: Number(f.sort_order) || 0,
      free_over: f.free_over === "" || f.free_over === null ? null : Number(f.free_over),
    };
    try {
      if (shipMethodDialog.editing) await api.put(`/admin/shipping-methods/${shipMethodDialog.editing.id}`, payload);
      else await api.post("/admin/shipping-methods", payload);
      toast.success(shipMethodDialog.editing ? "Shipping method updated" : "Shipping method created");
      setShipMethodDialog({ open: false, editing: null, form: emptyShip });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggleShipMethod = async (m) => {
    try {
      await api.put(`/admin/shipping-methods/${m.id}`, { name: m.name, carrier: m.carrier || "", price: Number(m.price) || 0, description: m.description || "", enabled: !m.enabled, sort_order: Number(m.sort_order) || 0, free_over: m.free_over ?? null });
      loadAll();
    } catch { toast.error("Update failed"); }
  };
  const deleteShipMethod = async (m) => {
    if (!window.confirm(`Delete ${m.name}?`)) return;
    try { await api.delete(`/admin/shipping-methods/${m.id}`); toast.success("Shipping method deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  const openDoc = (path) => window.open(`${API}${path}`, "_blank");
  const openBookingDoc = (b) => setBookingDoc({ open: true, booking: b, amount: b.service_amount ?? "", description: b.service_description || "", scheduled_at: (b.scheduled_at || "").slice(0, 16), duration_min: b.duration_min || 30 });
  const saveAndDownloadInvoice = async () => {
    const b = bookingDoc.booking;
    if (!bookingDoc.amount || Number(bookingDoc.amount) <= 0) { toast.error("Enter an invoice amount"); return; }
    try {
      await api.put(`/admin/bookings/${b.id}/invoice`, { amount: Number(bookingDoc.amount), description: bookingDoc.description });
      toast.success("Invoice saved"); loadAll();
      openDoc(`/admin/bookings/${b.id}/invoice`);
    } catch { toast.error("Could not save invoice"); }
  };
  const saveAndDownloadIcs = async () => {
    const b = bookingDoc.booking;
    if (!bookingDoc.scheduled_at) { toast.error("Pick a date & time"); return; }
    try {
      await api.put(`/admin/bookings/${b.id}/schedule`, { scheduled_at: bookingDoc.scheduled_at, duration_min: Number(bookingDoc.duration_min) || 30 });
      toast.success("Appointment scheduled — reminder added to the .ics"); loadAll();
      openDoc(`/admin/bookings/${b.id}/calendar.ics`);
    } catch { toast.error("Could not schedule"); }
  };
  const addAdmin = async () => {
    const { name, email, password } = userDialog;
    if (!name || !email || password.length < 8) { toast.error("Name, email and an 8+ character password are required"); return; }
    try {
      await api.post("/admin/users", { name, email, password });
      toast.success("Administrator added");
      setUserDialog({ open: false, name: "", email: "", password: "" });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add administrator"); }
  };
  const toggleRole = async (u) => {
    try { await api.put(`/admin/users/${u.id}/role`, { is_admin: !u.is_admin }); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update role"); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This permanently removes their account and cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete user"); }
  };

  const openPromo = (c) => setPromoDialog({ open: true, client: c, percent: 10, note: "", sending: false });
  const sendPromo = async () => {
    const { client, percent, note } = promoDialog;
    if (!percent || Number(percent) <= 0 || Number(percent) > 100) { toast.error("Enter a percentage between 1 and 100"); return; }
    setPromoDialog((d) => ({ ...d, sending: true }));
    try {
      const { data } = await api.post(`/admin/users/${client.id}/send-discount`, { percent: Number(percent), note });
      toast.success(`Code ${data.code} emailed to ${client.email}`);
      setPromoDialog({ open: false, client: null, percent: 10, note: "", sending: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send promo");
      setPromoDialog((d) => ({ ...d, sending: false }));
    }
  };
  const saveTimeout = async () => {
    setSavingTimeout(true);
    try {
      await api.put("/admin/settings/session", { timeout_minutes: Number(sessionTimeout) || 0 });
      toast.success(Number(sessionTimeout) > 0 ? `Auto-logout set to ${sessionTimeout} minutes` : "Auto-logout disabled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save setting"); }
    finally { setSavingTimeout(false); }
  };
  const saveRewards = async () => {
    setSavingRewards(true);
    try {
      await api.put("/admin/settings/rewards", {
        enabled: !!rewardsCfg.enabled,
        earn_spend: Number(rewardsCfg.earn_spend) || 0,
        earn_reward: Number(rewardsCfg.earn_reward) || 0,
        min_redeem: Number(rewardsCfg.min_redeem) || 0,
        expiry_months: Number(rewardsCfg.expiry_months) || 0,
      });
      toast.success("Rewards program saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save rewards"); }
    finally { setSavingRewards(false); }
  };
  const toggleUpsellProduct = (slug) => setUpsellCfg((c) => ({
    ...c,
    product_slugs: c.product_slugs.includes(slug) ? c.product_slugs.filter((s) => s !== slug) : [...c.product_slugs, slug],
  }));
  const saveUpsell = async () => {
    setSavingUpsell(true);
    try {
      await api.put("/admin/settings/upsell", { enabled: !!upsellCfg.enabled, product_slugs: upsellCfg.product_slugs });
      toast.success("Checkout add-ons saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save add-ons"); }
    finally { setSavingUpsell(false); }
  };
  const saveSale = async () => {
    setSavingSale(true);
    try {
      await api.put("/admin/settings/sale", {
        enabled: !!saleCfg.enabled,
        name: saleCfg.name || "",
        emoji: saleCfg.emoji || "",
        message: saleCfg.message || "",
        type: saleCfg.type || "percent",
        value: Number(saleCfg.value) || 0,
        allow_discount_codes: !!saleCfg.allow_discount_codes,
        code_combine: saleCfg.code_combine || "best",
        start_at: saleCfg.start_at || null,
        end_at: saleCfg.end_at || null,
      });
      toast.success("Store-wide sale saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save sale"); }
    finally { setSavingSale(false); }
  };
  const openPoints = (c) => setPointsDialog({ open: true, client: c, amount: 0, note: "", saving: false });
  const savePoints = async () => {
    const { client, amount, note } = pointsDialog;
    if (!amount || Number(amount) === 0) { toast.error("Enter a non-zero dollar amount"); return; }
    setPointsDialog((d) => ({ ...d, saving: true }));
    try {
      const { data } = await api.post(`/admin/users/${client.id}/reward`, { amount: Number(amount), note });
      toast.success(`Balance updated to $${data.balance.toFixed(2)}`);
      setPointsDialog({ open: false, client: null, amount: 0, note: "", saving: false });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not adjust rewards");
      setPointsDialog((d) => ({ ...d, saving: false }));
    }
  };

  const openBookingEmail = (b) => setEmailDialog({ open: true, booking: b, subject: `Regarding your ${b.program} consultation`, message: "", sending: false });
  const sendBookingEmail = async () => {
    const { booking, subject, message } = emailDialog;
    if (!subject || !message) { toast.error("Subject and message are required"); return; }
    setEmailDialog((d) => ({ ...d, sending: true }));
    try {
      await api.post(`/admin/bookings/${booking.id}/email`, { subject, message });
      toast.success(`Email sent to ${booking.email}`);
      setEmailDialog({ open: false, booking: null, subject: "", message: "", sending: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
      setEmailDialog((d) => ({ ...d, sending: false }));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const clients = users.filter((u) => !u.is_admin);
  const filteredClients = clients.filter((c) => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return true;
    return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q);
  });

  return (
    <>
      <Seo title="Admin — Just Peptides Supply" path="/admin" noindex />
      <div className="relative overflow-hidden bg-brand-ink text-white">
        <img src={HERO_BG} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/85 to-brand-ink/60" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 h-28 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative inline-block">
              <span aria-hidden="true" className="absolute left-1/2 top-1/2 h-16 w-56 -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-white blur-2xl opacity-90" />
              <BrandLogo h={72} className="relative" />
            </div>
            <span className="font-mono-label text-xs uppercase tracking-[0.2em] text-brand-teal">Admin</span>
          </div>
          <Button onClick={() => { logout(); navigate("/"); }} variant="outline" className="rounded-full gap-2 bg-white/5 border-white/20 text-white hover:bg-white/15" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Sign Out
          </Button>
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-10">
        {stats && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-10">
            <Stat icon={DollarSign} label="Revenue (paid)" value={`$${stats.revenue.toFixed(2)}`} />
            <Stat icon={ShoppingBag} label="Orders" value={stats.orders} />
            <Stat icon={Package} label="Products" value={stats.products} />
            <Stat icon={CalendarClock} label="Bookings" value={stats.bookings} />
          </div>
        )}

        <Tabs defaultValue="products">
          <TabsList>
            <TabsTrigger value="products" data-testid="admin-tab-products"><Package className="h-4 w-4 mr-2" /> Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders"><ShoppingBag className="h-4 w-4 mr-2" /> Orders</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings"><CalendarClock className="h-4 w-4 mr-2" /> Telehealth</TabsTrigger>
            <TabsTrigger value="carts" data-testid="admin-tab-carts"><ShoppingBasket className="h-4 w-4 mr-2" /> Abandoned Carts</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="admin-tab-discounts"><Ticket className="h-4 w-4 mr-2" /> Discounts</TabsTrigger>
            <TabsTrigger value="shipping" data-testid="admin-tab-shipping"><Truck className="h-4 w-4 mr-2" /> Shipping</TabsTrigger>
            <TabsTrigger value="team" data-testid="admin-tab-team"><Users className="h-4 w-4 mr-2" /> Team</TabsTrigger>
            <TabsTrigger value="clients" data-testid="admin-tab-clients"><UserRound className="h-4 w-4 mr-2" /> Clients</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings"><Clock className="h-4 w-4 mr-2" /> Settings</TabsTrigger>
          </TabsList>

          {/* Products */}
          <TabsContent value="products">
            <div className="flex justify-end mb-4">
              <Button onClick={openNew} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white gap-2" data-testid="admin-add-product">
                <Plus className="h-4 w-4" /> Add Product
              </Button>
            </div>
            <div className="rounded-xl border border-brand-navy/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-brand-mist text-brand-navy/60 font-mono-label text-[11px] uppercase tracking-[0.1em]">
                  <tr><th className="text-left p-4">Product</th><th className="text-left p-4">Category</th><th className="text-left p-4">Price</th><th className="text-left p-4">Stock</th><th className="text-right p-4">Actions</th></tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-brand-navy/10" data-testid={`admin-product-${p.slug}`}>
                      <td className="p-4 flex items-center gap-3">
                        <img src={p.image} alt="" className="h-10 w-10 rounded object-cover bg-brand-mist" />
                        <span className="font-medium text-brand-navy">{p.name}</span>
                      </td>
                      <td className="p-4 text-brand-navy/60">{p.category}</td>
                      <td className="p-4 font-semibold text-brand-navy">${p.price.toFixed(2)}</td>
                      <td className="p-4" data-testid={`product-stock-${p.slug}`}>
                        {p.stock > 0 ? (
                          <span className={`font-semibold ${p.stock <= 5 ? "text-orange-500" : "text-brand-navy"}`}>{p.stock}{p.stock <= 5 ? " (low)" : ""}</span>
                        ) : (
                          <span className="font-mono-label text-[10px] uppercase bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">Sold out</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <button onClick={() => openEdit(p)} className="p-2 text-brand-navy/60 hover:text-brand-blue" data-testid={`edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(p)} className="p-2 text-brand-navy/60 hover:text-destructive" data-testid={`delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders">
            {orders.length === 0 ? <Empty text="No orders yet." /> : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} className="rounded-xl border border-brand-navy/10 bg-white p-5" data-testid={`admin-order-${o.id}`}>
                    <div className="flex justify-between items-center flex-wrap gap-2">
                      <div>
                        <p className="font-semibold text-brand-navy">{o.email || "guest"}</p>
                        <p className="text-xs text-brand-navy/50">#{o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleString()} · {o.shipping_name} · {o.shipping_address}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-display text-xl text-brand-navy">${o.amount.toFixed(2)}</p>
                        <span className="font-mono-label text-[10px] uppercase bg-brand-teal/15 text-brand-teal px-2 py-0.5 rounded-full">{o.status}</span>
                        {o.payment_method === "manual" && <span className="ml-1 font-mono-label text-[10px] uppercase bg-brand-navy/10 text-brand-navy/70 px-2 py-0.5 rounded-full">manual</span>}
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-brand-navy/60">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</div>
                    <div className="mt-2" data-testid={`order-email-status-${o.id}`}>
                      {o.confirmation_email_status === "sent" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-teal">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Confirmation sent{o.confirmation_email_at ? ` · ${new Date(o.confirmation_email_at).toLocaleDateString()}` : ""}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                          <Mail className="h-3.5 w-3.5" /> {o.confirmation_email_status === "failed" ? "Confirmation failed — resend" : "Confirmation not sent"}
                        </span>
                      )}
                    </div>
                    {o.tracking_number && (
                      <p className="mt-2 text-xs text-brand-navy/60" data-testid={`order-tracking-${o.id}`}>
                        <Truck className="inline h-3.5 w-3.5 mr-1 text-brand-teal" />Tracking: <span className="font-semibold text-brand-navy">{o.tracking_number}</span>{o.carrier ? ` · ${o.carrier}` : ""}
                      </p>
                    )}

                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                      <div className="flex-1">
                        <Label className="text-xs text-brand-navy/50">Notes</Label>
                        <Textarea rows={2} value={noteVal(o)} onChange={setNote(o.id)} className="mt-1" placeholder="Internal notes for this order…" data-testid={`order-notes-${o.id}`} />
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        {o.status !== "paid" && o.status !== "shipped" && (
                          <Button onClick={() => markPaid(o)} size="sm" className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white gap-1.5" data-testid={`order-mark-paid-${o.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Mark Paid</Button>
                        )}
                        {o.status !== "shipped" && (
                          <Button onClick={() => openShip(o)} size="sm" className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white gap-1.5" data-testid={`order-ship-${o.id}`}><Truck className="h-3.5 w-3.5" /> {o.tracking_number ? "Update Shipping" : "Mark Shipped"}</Button>
                        )}
                        <Button onClick={() => saveOrderNotes(o.id)} variant="outline" size="sm" className="rounded-full" data-testid={`order-save-notes-${o.id}`}>Save Notes</Button>
                        <Button onClick={() => openDoc(`/admin/orders/${o.id}/invoice`)} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`order-invoice-${o.id}`}><FileText className="h-3.5 w-3.5" /> {["paid", "shipped", "fulfilled", "completed"].includes(o.status) ? "Receipt" : "Invoice"}</Button>
                        <Button onClick={() => emailOrder(o)} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`order-email-${o.id}`}><Mail className="h-3.5 w-3.5" /> Resend Confirmation</Button>
                        <Button onClick={() => deleteOrder(o)} variant="outline" size="sm" className="rounded-full text-destructive hover:text-destructive gap-1.5" data-testid={`order-delete-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Bookings */}
          <TabsContent value="bookings">
            {bookings.length === 0 ? <Empty text="No telehealth bookings yet." /> : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <div key={b.id} className="rounded-xl border border-brand-navy/10 bg-white p-5" data-testid={`admin-booking-${b.id}`}>
                    <div className="flex justify-between items-start flex-wrap gap-3">
                      <div>
                        <p className="font-semibold text-brand-navy">{b.name} <span className="font-normal text-brand-navy/50">· {b.program}</span></p>
                        <p className="text-xs text-brand-navy/50 mt-0.5">{b.email} · {b.phone}</p>
                        <p className="text-sm text-brand-navy/70 mt-2">{b.goals}</p>
                        {b.preferred_time && <p className="text-xs text-brand-navy/50 mt-1">Preferred: {b.preferred_time}</p>}
                        {b.scheduled_at && <p className="text-xs text-brand-teal mt-1" data-testid={`booking-scheduled-${b.id}`}>Scheduled: {new Date(b.scheduled_at).toLocaleString()}</p>}
                        {!b.scheduled_at && b.booking_reminded_at && (
                          <p className="text-xs text-brand-navy/45 mt-1" data-testid={`booking-reminded-${b.id}`}>Reminder sent · {new Date(b.booking_reminded_at).toLocaleDateString()}</p>
                        )}
                        {b.service_amount != null && <p className="text-xs text-brand-navy/50 mt-0.5">Invoiced: ${Number(b.service_amount).toFixed(2)}</p>}
                        {b.scheduled_at && (
                          <div className="mt-1.5" data-testid={`booking-email-status-${b.id}`}>
                            {b.confirmation_email_status === "sent" ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-teal">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Invite sent{b.confirmation_email_at ? ` · ${new Date(b.confirmation_email_at).toLocaleDateString()}` : ""}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                                <Mail className="h-3.5 w-3.5" /> {b.confirmation_email_status === "failed" ? "Invite failed — resend" : "Invite not sent"}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <select value={b.status} onChange={(e) => updateBooking(b.id, e.target.value)} className="rounded-full border border-brand-navy/15 px-3 py-1.5 text-sm" data-testid={`booking-status-${b.id}`}>
                        <option value="requested">Requested</option>
                        <option value="contacted">Contacted</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>

                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                      <div className="flex-1">
                        <Label className="text-xs text-brand-navy/50">Notes</Label>
                        <Textarea rows={2} value={noteVal(b)} onChange={setNote(b.id)} className="mt-1" placeholder="Notes about this client / issue…" data-testid={`booking-notes-${b.id}`} />
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button onClick={() => saveBookingNotes(b.id)} variant="outline" size="sm" className="rounded-full" data-testid={`booking-save-notes-${b.id}`}>Save Notes</Button>
                        <Button onClick={() => openBookingDoc(b)} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`booking-doc-${b.id}`}><FileText className="h-3.5 w-3.5" /> Invoice / Schedule</Button>
                        <Button onClick={() => openBookingEmail(b)} size="sm" className="rounded-full bg-brand-navy hover:bg-brand-blue text-white gap-1.5" data-testid={`booking-email-${b.id}`}><Mail className="h-3.5 w-3.5" /> Email</Button>
                        {b.scheduled_at && <Button onClick={() => resendBookingConfirmation(b)} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`booking-resend-${b.id}`}><CalendarClock className="h-3.5 w-3.5" /> Resend Invite</Button>}
                        {!b.scheduled_at && <Button onClick={() => nudgeBooking(b)} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`booking-nudge-${b.id}`}><Send className="h-3.5 w-3.5" /> Send Reminder</Button>}
                        <Button onClick={() => deleteBooking(b)} variant="outline" size="sm" className="rounded-full text-destructive hover:text-destructive gap-1.5" data-testid={`booking-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Abandoned Carts */}
          <TabsContent value="carts">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-brand-navy/55">Automatic reminders send after ~1 hour of inactivity. You can also act manually below.</p>
              <Button onClick={sendAllReminders} variant="outline" className="rounded-full gap-2" data-testid="carts-send-all"><Mail className="h-4 w-4" /> Send All Reminders</Button>
            </div>
            {carts.length === 0 ? <Empty text="No abandoned carts right now." /> : (
              <div className="space-y-3">
                {carts.map((c) => (
                  <div key={c.id} className="rounded-xl border border-brand-navy/10 bg-white p-5" data-testid={`admin-cart-${c.id}`}>
                    <div className="flex justify-between items-start flex-wrap gap-3">
                      <div>
                        <p className="font-semibold text-brand-navy">{c.email}</p>
                        <p className="text-xs text-brand-navy/50 mt-0.5">Last active {new Date(c.updated_at).toLocaleString()}{c.reminded_at ? " · reminder sent" : ""}</p>
                        <p className="text-sm text-brand-navy/60 mt-2">{(c.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-display text-lg text-brand-navy">${cartSubtotal(c).toFixed(2)}</p>
                        <div className="flex gap-2 mt-2">
                          <Button onClick={() => remindCart(c)} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`cart-remind-${c.id}`}><Mail className="h-3.5 w-3.5" /> Remind</Button>
                          <Button onClick={() => openWinback(c)} size="sm" className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white gap-1.5" data-testid={`cart-winback-${c.id}`}><Ticket className="h-3.5 w-3.5" /> Send Discount</Button>
                          <Button onClick={() => deleteCart(c)} variant="outline" size="sm" className="rounded-full text-destructive hover:text-destructive gap-1.5" data-testid={`cart-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Discounts */}
          <TabsContent value="discounts">
            <div className="rounded-xl border border-brand-navy/10 bg-white p-5 mb-5">
              <h3 className="font-display text-lg text-brand-navy mb-3">Create a discount code</h3>
              <div className="grid gap-3 sm:grid-cols-[1.4fr_1fr_1fr_auto] sm:items-end">
                <div><Label className="text-xs">Code</Label><Input value={discForm.code} onChange={(e) => setDiscForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="WELCOME10" className="mt-1" data-testid="disc-code" /></div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <select value={discForm.type} onChange={(e) => setDiscForm((f) => ({ ...f, type: e.target.value }))} className="mt-1 w-full rounded-md border border-brand-navy/15 px-3 py-2 text-sm h-10" data-testid="disc-type">
                    <option value="percent">Percent (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
                <div><Label className="text-xs">Value</Label><Input type="number" step="0.01" value={discForm.value} onChange={(e) => setDiscForm((f) => ({ ...f, value: e.target.value }))} className="mt-1" data-testid="disc-value" /></div>
                <Button onClick={createDiscount} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white gap-2 h-10" data-testid="disc-create"><Plus className="h-4 w-4" /> Create</Button>
              </div>
            </div>
            {discounts.length === 0 ? <Empty text="No discount codes yet." /> : (
              <div className="rounded-xl border border-brand-navy/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-brand-mist text-brand-navy/60 font-mono-label text-[11px] uppercase tracking-[0.1em]">
                    <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Discount</th><th className="text-left p-4">Uses</th><th className="text-right p-4">Actions</th></tr>
                  </thead>
                  <tbody>
                    {discounts.map((d) => (
                      <tr key={d.id} className="border-t border-brand-navy/10" data-testid={`admin-discount-${d.code}`}>
                        <td className="p-4 font-semibold text-brand-navy">{d.code}</td>
                        <td className="p-4 text-brand-navy/70">{d.type === "percent" ? `${d.value}% off` : `$${Number(d.value).toFixed(2)} off`}</td>
                        <td className="p-4 text-brand-navy/60">{d.uses || 0}{d.max_uses ? ` / ${d.max_uses}` : ""}</td>
                        <td className="p-4 text-right">
                          <button onClick={() => deleteDiscount(d)} className="p-2 text-brand-navy/60 hover:text-destructive" data-testid={`discount-delete-${d.code}`}><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Shipping */}
          <TabsContent value="shipping">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <p className="text-sm text-brand-navy/60 max-w-xl">Configure the shipping methods and rates customers choose at checkout. Set an optional “free over” threshold per method. Print labels in Rollo after the order is paid.</p>
              <Button onClick={() => openShipMethod(null)} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white gap-2 shrink-0" data-testid="admin-add-shipping"><Plus className="h-4 w-4" /> Add Method</Button>
            </div>
            {shipMethods.length === 0 ? <Empty text="No shipping methods yet. Add one so customers can check out." /> : (
              <div className="rounded-xl border border-brand-navy/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-brand-mist text-brand-navy/60 font-mono-label text-[11px] uppercase tracking-[0.1em]">
                    <tr><th className="text-left p-4">Method</th><th className="text-left p-4">Carrier</th><th className="text-left p-4">Price</th><th className="text-left p-4">Free over</th><th className="text-left p-4">Visible</th><th className="text-right p-4">Actions</th></tr>
                  </thead>
                  <tbody>
                    {shipMethods.map((m) => (
                      <tr key={m.id} className="border-t border-brand-navy/10" data-testid={`admin-shipping-${m.id}`}>
                        <td className="p-4">
                          <p className="font-semibold text-brand-navy">{m.name}</p>
                          {m.description && <p className="text-xs text-brand-navy/50">{m.description}</p>}
                        </td>
                        <td className="p-4 text-brand-navy/70">{m.carrier || "—"}</td>
                        <td className="p-4 text-brand-navy/70">${Number(m.price).toFixed(2)}</td>
                        <td className="p-4 text-brand-navy/70">{m.free_over != null ? `$${Number(m.free_over).toFixed(2)}` : "—"}</td>
                        <td className="p-4"><Switch checked={m.enabled} onCheckedChange={() => toggleShipMethod(m)} data-testid={`shipping-toggle-${m.id}`} /></td>
                        <td className="p-4 text-right whitespace-nowrap">
                          <button onClick={() => openShipMethod(m)} className="p-2 text-brand-navy/60 hover:text-brand-blue" data-testid={`shipping-edit-${m.id}`}><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => deleteShipMethod(m)} className="p-2 text-brand-navy/60 hover:text-destructive" data-testid={`shipping-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Team / Administrators */}
          <TabsContent value="team">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <p className="text-sm text-brand-navy/60 max-w-xl">Create and manage website administrators. Toggle a user's access on or off at any time.</p>
              <Button onClick={() => setUserDialog({ open: true, name: "", email: "", password: "" })} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white gap-2 shrink-0" data-testid="admin-add-user"><Plus className="h-4 w-4" /> Add Administrator</Button>
            </div>
            <div className="rounded-xl border border-brand-navy/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-brand-mist text-brand-navy/60 font-mono-label text-[11px] uppercase tracking-[0.1em]">
                  <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Joined</th><th className="text-center p-4">Administrator</th><th className="text-right p-4">Actions</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-brand-navy/10" data-testid={`admin-user-${u.id}`}>
                      <td className="p-4 font-semibold text-brand-navy">{u.name || "—"}</td>
                      <td className="p-4 text-brand-navy/70">{u.email}</td>
                      <td className="p-4 text-brand-navy/60">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                      <td className="p-4 text-center"><Switch checked={!!u.is_admin} onCheckedChange={() => toggleRole(u)} data-testid={`user-admin-toggle-${u.id}`} /></td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => deleteUser(u)}
                          disabled={u.id === user.id}
                          title={u.id === user.id ? "You cannot delete your own account" : "Delete user"}
                          className="p-2 text-brand-navy/60 hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-brand-navy/60"
                          data-testid={`user-delete-${u.id}`}
                        ><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Clients */}
          <TabsContent value="clients">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm text-brand-navy/60 max-w-xl">Your registered customers. Search and send a personalized single-use promo code straight to their inbox.</p>
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-navy/40" />
                <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Search name or email…" className="pl-9 rounded-full" data-testid="client-search" />
              </div>
            </div>
            {filteredClients.length === 0 ? <Empty text={clients.length === 0 ? "No customers have registered yet." : "No customers match your search."} /> : (
              <div className="rounded-xl border border-brand-navy/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-brand-mist text-brand-navy/60 font-mono-label text-[11px] uppercase tracking-[0.1em]">
                    <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Joined</th><th className="text-left p-4">Rewards</th><th className="text-right p-4">Actions</th></tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((c) => (
                      <tr key={c.id} className="border-t border-brand-navy/10" data-testid={`admin-client-${c.id}`}>
                        <td className="p-4 font-semibold text-brand-navy">{c.name || "—"}</td>
                        <td className="p-4 text-brand-navy/70">{c.email}</td>
                        <td className="p-4 text-brand-navy/60">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                        <td className="p-4 font-semibold text-brand-teal" data-testid={`client-points-${c.id}`}>${Number(c.reward_balance || 0).toFixed(2)}</td>
                        <td className="p-4 text-right whitespace-nowrap">
                          <Button onClick={() => openPromo(c)} size="sm" className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white gap-1.5" data-testid={`client-promo-${c.id}`}><Gift className="h-3.5 w-3.5" /> Send Promo</Button>
                          <Button onClick={() => openPoints(c)} size="sm" variant="outline" className="ml-1 rounded-full gap-1.5" data-testid={`client-points-adjust-${c.id}`}><Star className="h-3.5 w-3.5" /> Rewards</Button>
                          <button onClick={() => deleteUser(c)} title="Delete customer" className="ml-1 p-2 align-middle text-brand-navy/60 hover:text-destructive" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            <div className="max-w-lg rounded-xl border border-brand-navy/10 bg-white p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-teal/15"><Clock className="h-4 w-4 text-brand-teal" /></div>
                <h3 className="font-display text-lg text-brand-navy">Auto-logout for inactivity</h3>
              </div>
              <p className="text-sm text-brand-navy/60 mb-4">Automatically sign out any inactive user after this many minutes. A 30-second warning is shown first so they can stay signed in. Set to 0 to disable.</p>
              <div className="flex items-end gap-3">
                <div className="flex-1 max-w-[180px]">
                  <Label className="text-xs">Timeout (minutes)</Label>
                  <Input type="number" min="0" step="1" value={sessionTimeout} onChange={(e) => setSessionTimeout(e.target.value)} className="mt-1.5" data-testid="session-timeout-input" />
                </div>
                <Button onClick={saveTimeout} disabled={savingTimeout} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white h-10" data-testid="session-timeout-save">{savingTimeout ? "Saving…" : "Save"}</Button>
              </div>
            </div>

            {/* Rewards program */}
            <div className="mt-6 max-w-2xl rounded-xl border border-brand-navy/10 bg-white p-6">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-teal/15"><Gift className="h-4 w-4 text-brand-teal" /></div>
                  <h3 className="font-display text-lg text-brand-navy">Customer rewards program</h3>
                </div>
                <label className="flex items-center gap-2">
                  <span className="text-sm text-brand-navy/60">{rewardsCfg.enabled ? "Active" : "Off"}</span>
                  <Switch checked={rewardsCfg.enabled} onCheckedChange={(v) => setRewardsCfg((c) => ({ ...c, enabled: v }))} data-testid="rewards-enabled" />
                </label>
              </div>
              <p className="text-sm text-brand-navy/60 mb-4">Registered customers earn a reward credit on paid orders and redeem it for money off future purchases. Credit is added when a manual order is marked paid, or immediately for card orders.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="text-xs">Customer spends ($)</Label><Input type="number" step="1" min="1" value={rewardsCfg.earn_spend} onChange={(e) => setRewardsCfg((c) => ({ ...c, earn_spend: e.target.value }))} className="mt-1.5" data-testid="rewards-earn-spend" /></div>
                <div><Label className="text-xs">…and earns this reward ($)</Label><Input type="number" step="0.01" min="0" value={rewardsCfg.earn_reward} onChange={(e) => setRewardsCfg((c) => ({ ...c, earn_reward: e.target.value }))} className="mt-1.5" data-testid="rewards-earn-reward" /></div>
                <div><Label className="text-xs">Minimum reward to redeem ($)</Label><Input type="number" step="0.01" min="0" value={rewardsCfg.min_redeem} onChange={(e) => setRewardsCfg((c) => ({ ...c, min_redeem: e.target.value }))} className="mt-1.5" data-testid="rewards-min-redeem" /></div>
                <div><Label className="text-xs">Rewards expire after (months, 0 = never)</Label><Input type="number" step="1" min="0" value={rewardsCfg.expiry_months} onChange={(e) => setRewardsCfg((c) => ({ ...c, expiry_months: e.target.value }))} className="mt-1.5" data-testid="rewards-expiry" /></div>
              </div>
              <p className="mt-3 text-xs text-brand-navy/45">In plain terms: spend ${Number(rewardsCfg.earn_spend || 0).toFixed(2)}, get ${Number(rewardsCfg.earn_reward || 0).toFixed(2)} back{Number(rewardsCfg.earn_spend) > 0 ? ` (${((Number(rewardsCfg.earn_reward) / Number(rewardsCfg.earn_spend)) * 100).toFixed(1)}% back)` : ""}. A $150 order earns ${(150 * (Number(rewardsCfg.earn_reward || 0) / (Number(rewardsCfg.earn_spend) || 1))).toFixed(2)}.</p>
              <Button onClick={saveRewards} disabled={savingRewards} className="mt-4 rounded-full bg-brand-navy hover:bg-brand-blue text-white h-10" data-testid="rewards-save">{savingRewards ? "Saving…" : "Save Rewards Program"}</Button>
            </div>

            {/* Store-wide sale */}
            <div className="mt-6 max-w-2xl rounded-xl border border-brand-navy/10 bg-white p-6" data-testid="sale-config-card">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100"><Star className="h-4 w-4 text-amber-600" /></div>
                  <h3 className="font-display text-lg text-brand-navy">Store-wide sale</h3>
                </div>
                <label className="flex items-center gap-2">
                  <span className="text-sm text-brand-navy/60">{saleCfg.enabled ? "On" : "Off"}</span>
                  <Switch checked={saleCfg.enabled} onCheckedChange={(v) => setSaleCfg((c) => ({ ...c, enabled: v }))} data-testid="sale-enabled" />
                </label>
              </div>
              <p className="text-sm text-brand-navy/60 mb-4">Run a site-wide discount (e.g. a July 4th Sale) applied automatically to every cart, with a banner across the site. Optionally schedule a start &amp; end (times in UTC) and it turns on and off by itself.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label className="text-xs">Sale name</Label><Input value={saleCfg.name} onChange={(e) => setSaleCfg((c) => ({ ...c, name: e.target.value }))} placeholder="July 4th Sale" className="mt-1.5" data-testid="sale-name" /></div>
                <div>
                  <Label className="text-xs">Discount type</Label>
                  <select value={saleCfg.type} onChange={(e) => setSaleCfg((c) => ({ ...c, type: e.target.value }))} className="mt-1.5 h-10 w-full rounded-md border border-brand-navy/20 bg-white px-3 text-sm" data-testid="sale-type">
                    <option value="percent">Percent off (%)</option>
                    <option value="fixed">Fixed amount off ($)</option>
                  </select>
                </div>
                <div><Label className="text-xs">{saleCfg.type === "percent" ? "Percent off (%)" : "Amount off ($)"}</Label><Input type="number" step="0.01" min="0" value={saleCfg.value} onChange={(e) => setSaleCfg((c) => ({ ...c, value: e.target.value }))} className="mt-1.5" data-testid="sale-value" /></div>
                <div><Label className="text-xs">Emoji (optional)</Label><Input value={saleCfg.emoji} onChange={(e) => setSaleCfg((c) => ({ ...c, emoji: e.target.value }))} placeholder="🎉" className="mt-1.5" data-testid="sale-emoji" /></div>
                <div className="hidden sm:block" />
                <div><Label className="text-xs">Starts (optional)</Label><Input type="datetime-local" value={saleCfg.start_at} onChange={(e) => setSaleCfg((c) => ({ ...c, start_at: e.target.value }))} className="mt-1.5" data-testid="sale-start" /></div>
                <div><Label className="text-xs">Ends (optional)</Label><Input type="datetime-local" value={saleCfg.end_at} onChange={(e) => setSaleCfg((c) => ({ ...c, end_at: e.target.value }))} className="mt-1.5" data-testid="sale-end" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Custom banner message (optional — overrides the auto text)</Label><Input value={saleCfg.message} onChange={(e) => setSaleCfg((c) => ({ ...c, message: e.target.value }))} placeholder="Auto-built from the name + discount if left blank" className="mt-1.5" data-testid="sale-message" /></div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-brand-navy/10 bg-brand-mist px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-brand-navy">Allow promo codes during the sale</p>
                  <p className="text-xs text-brand-navy/55">If off, customers can't apply discount codes while the sale runs.</p>
                </div>
                <Switch checked={saleCfg.allow_discount_codes} onCheckedChange={(v) => setSaleCfg((c) => ({ ...c, allow_discount_codes: v }))} data-testid="sale-allow-codes" />
              </div>
              {saleCfg.allow_discount_codes && (
                <div className="mt-3">
                  <Label className="text-xs">When a code is used with the sale</Label>
                  <select value={saleCfg.code_combine} onChange={(e) => setSaleCfg((c) => ({ ...c, code_combine: e.target.value }))} className="mt-1.5 h-10 w-full rounded-md border border-brand-navy/20 bg-white px-3 text-sm" data-testid="sale-code-combine">
                    <option value="best">Use whichever is bigger (sale or code)</option>
                    <option value="stack">Stack them — sale first, then the code</option>
                  </select>
                </div>
              )}
              <div className="mt-4 rounded-lg bg-brand-navy px-4 py-2.5 text-center text-sm font-semibold text-white" data-testid="sale-banner-preview">
                {(() => {
                  const c = saleCfg;
                  const base = c.message ? c.message : `${c.name || "Store-wide Sale"} — ${c.type === "percent" ? `${Number(c.value) || 0}% off everything` : `$${(Number(c.value) || 0).toFixed(2)} off your order`}!`;
                  return `${c.emoji ? c.emoji + " " : ""}${base}`;
                })()}
              </div>
              <Button onClick={saveSale} disabled={savingSale} className="mt-4 rounded-full bg-brand-navy hover:bg-brand-blue text-white h-10" data-testid="sale-save">{savingSale ? "Saving…" : "Save Sale"}</Button>
            </div>

            {/* Checkout add-ons (upsell) */}
            <div className="mt-6 max-w-2xl rounded-xl border border-brand-navy/10 bg-white p-6">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-blue/15"><Sparkles className="h-4 w-4 text-brand-blue" /></div>
                  <h3 className="font-display text-lg text-brand-navy">Pre-checkout add-ons</h3>
                </div>
                <label className="flex items-center gap-2">
                  <span className="text-sm text-brand-navy/60">{upsellCfg.enabled ? "Active" : "Off"}</span>
                  <Switch checked={upsellCfg.enabled} onCheckedChange={(v) => setUpsellCfg((c) => ({ ...c, enabled: v }))} data-testid="upsell-enabled" />
                </label>
              </div>
              <p className="text-sm text-brand-navy/60 mb-4">Choose products to suggest in a popup before the customer checks out (e.g. Bacteriostatic Water). They can add them in one tap.</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {products.map((p) => (
                  <label key={p.id} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${upsellCfg.product_slugs.includes(p.slug) ? "border-brand-blue bg-brand-blue/5" : "border-brand-navy/15 hover:border-brand-navy/30"}`} data-testid={`upsell-select-${p.slug}`}>
                    <input type="checkbox" checked={upsellCfg.product_slugs.includes(p.slug)} onChange={() => toggleUpsellProduct(p.slug)} className="h-4 w-4 accent-brand-blue" />
                    <img src={p.image} alt="" className="h-8 w-8 rounded object-cover bg-brand-mist" />
                    <span className="text-sm font-medium text-brand-navy truncate">{p.name}</span>
                    <span className="ml-auto text-sm text-brand-navy/55">${Number(p.price).toFixed(2)}</span>
                  </label>
                ))}
              </div>
              <Button onClick={saveUpsell} disabled={savingUpsell} className="mt-4 rounded-full bg-brand-navy hover:bg-brand-blue text-white h-10" data-testid="upsell-save">{savingUpsell ? "Saving…" : "Save Add-ons"}</Button>
            </div>
          </TabsContent>

        </Tabs>
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-brand-navy">{editing ? "Edit Product" : "New Product"}</DialogTitle><DialogDescription className="sr-only">Fill in the product details and save.</DialogDescription></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="pf-name" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={set("slug")} className="mt-1.5" data-testid="pf-slug" /></div>
            <div><Label>Price ($)</Label><Input type="number" step="0.01" value={form.price} onChange={set("price")} className="mt-1.5" data-testid="pf-price" /></div>
            <div><Label>Stock quantity</Label><Input type="number" step="1" min="0" value={form.stock} onChange={set("stock")} className="mt-1.5" data-testid="pf-stock" placeholder="0 = sold out" /></div>
            <div><Label>Size</Label><Input value={form.size} onChange={set("size")} className="mt-1.5" data-testid="pf-size" /></div>
            <div><Label>Label name (short)</Label><Input value={form.label_name} onChange={set("label_name")} className="mt-1.5" placeholder="e.g. KLOW" data-testid="pf-label-name" /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={set("category")} className="mt-1.5" data-testid="pf-category" /></div>
            <div><Label>Purity</Label><Input value={form.purity} onChange={set("purity")} className="mt-1.5" data-testid="pf-purity" /></div>
            <div><Label>Image URL</Label><Input value={form.image} onChange={set("image")} className="mt-1.5" placeholder="Leave blank for default" data-testid="pf-image" /></div>
            <div className="sm:col-span-2">
              <Label>Certificate of Analysis (COA)</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={form.coa_url} onChange={set("coa_url")} className="flex-1" placeholder="Upload a file or paste a URL" data-testid="pf-coa" />
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-brand-navy/20 bg-brand-mist px-3 text-sm font-semibold text-brand-navy hover:bg-brand-navy/5 whitespace-nowrap">
                  {uploadingCoa ? "Uploading…" : "Upload"}
                  <input type="file" accept=".pdf,image/*" className="hidden" onChange={uploadCoa} data-testid="pf-coa-upload" disabled={uploadingCoa} />
                </label>
              </div>
              {form.coa_url && (
                <a href={resolveUrl(form.coa_url)} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-block text-xs text-brand-blue hover:underline" data-testid="pf-coa-preview">View current COA</a>
              )}
            </div>
            <div className="sm:col-span-2"><Label>Short description</Label><Input value={form.short} onChange={set("short")} className="mt-1.5" data-testid="pf-short" /></div>
            <div className="sm:col-span-2"><Label>Full description</Label><Textarea value={form.description} onChange={set("description")} rows={3} className="mt-1.5" data-testid="pf-description" /></div>
            <div className="sm:col-span-2"><Label>Reported benefits (one per line)</Label><Textarea value={form.benefits} onChange={set("benefits")} rows={4} className="mt-1.5" placeholder={"Improved sleep quality\nFat loss\nMuscle growth"} data-testid="pf-benefits" /></div>
            <label className="sm:col-span-2 flex items-center gap-3">
              <Switch checked={form.best_seller} onCheckedChange={(v) => setForm((f) => ({ ...f, best_seller: v }))} data-testid="pf-bestseller" />
              <span className="text-sm text-brand-navy">Mark as best seller</span>
            </label>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white w-full" data-testid="pf-save">{editing ? "Update" : "Create"} Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailDialog.open} onOpenChange={(v) => setEmailDialog((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-lg" data-testid="booking-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy">Email client</DialogTitle>
            <DialogDescription>{emailDialog.booking ? `To ${emailDialog.booking.name} · ${emailDialog.booking.email}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Subject</Label><Input value={emailDialog.subject} onChange={(e) => setEmailDialog((d) => ({ ...d, subject: e.target.value }))} className="mt-1.5" data-testid="booking-email-subject" /></div>
            <div><Label>Message</Label><Textarea rows={6} value={emailDialog.message} onChange={(e) => setEmailDialog((d) => ({ ...d, message: e.target.value }))} className="mt-1.5" placeholder="Write your message to the client…" data-testid="booking-email-message" /></div>
          </div>
          <DialogFooter>
            <Button onClick={sendBookingEmail} disabled={emailDialog.sending} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white w-full gap-2" data-testid="booking-email-send">
              <Send className="h-4 w-4" /> {emailDialog.sending ? "Sending…" : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shipDialog.open} onOpenChange={(v) => setShipDialog((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-md" data-testid="ship-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy">Mark as shipped</DialogTitle>
            <DialogDescription>{shipDialog.order ? `The customer (${shipDialog.order.email}) will be emailed the tracking details.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Tracking number</Label><Input value={shipDialog.tracking} onChange={(e) => setShipDialog((d) => ({ ...d, tracking: e.target.value }))} className="mt-1.5" placeholder="e.g. 9400 1000 0000 0000 0000 00" data-testid="ship-tracking" /></div>
            <div><Label>Carrier (optional)</Label><Input value={shipDialog.carrier} onChange={(e) => setShipDialog((d) => ({ ...d, carrier: e.target.value }))} className="mt-1.5" placeholder="USPS, UPS, FedEx…" data-testid="ship-carrier" /></div>
          </div>
          <DialogFooter>
            <Button onClick={submitShip} disabled={shipDialog.sending} className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white w-full gap-2" data-testid="ship-submit">
              <Truck className="h-4 w-4" /> {shipDialog.sending ? "Saving…" : "Mark Shipped & Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bookingDoc.open} onOpenChange={(v) => setBookingDoc((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-md" data-testid="booking-doc-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy">Invoice & Schedule</DialogTitle>
            <DialogDescription className="sr-only">Create a service invoice and schedule a calendar reminder for this booking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="rounded-xl border border-brand-navy/10 p-4">
              <p className="font-mono-label text-[11px] uppercase tracking-[0.16em] text-brand-navy/50 mb-3">Service Invoice</p>
              <div className="grid gap-3">
                <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={bookingDoc.amount} onChange={(e) => setBookingDoc((d) => ({ ...d, amount: e.target.value }))} className="mt-1.5" placeholder="150.00" data-testid="bdoc-amount" /></div>
                <div><Label>Description</Label><Input value={bookingDoc.description} onChange={(e) => setBookingDoc((d) => ({ ...d, description: e.target.value }))} className="mt-1.5" placeholder="e.g. Peptide + Training program (monthly)" data-testid="bdoc-desc" /></div>
                <Button onClick={saveAndDownloadInvoice} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white gap-2" data-testid="bdoc-invoice-save"><FileText className="h-4 w-4" /> Save & Download Invoice</Button>
              </div>
            </div>
            <div className="rounded-xl border border-brand-navy/10 p-4">
              <p className="font-mono-label text-[11px] uppercase tracking-[0.16em] text-brand-navy/50 mb-3">Appointment & Calendar Reminder</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label>Date & time</Label><Input type="datetime-local" value={bookingDoc.scheduled_at} onChange={(e) => setBookingDoc((d) => ({ ...d, scheduled_at: e.target.value }))} className="mt-1.5" data-testid="bdoc-datetime" /></div>
                <div><Label>Duration (min)</Label><Input type="number" value={bookingDoc.duration_min} onChange={(e) => setBookingDoc((d) => ({ ...d, duration_min: e.target.value }))} className="mt-1.5" data-testid="bdoc-duration" /></div>
              </div>
              <Button onClick={saveAndDownloadIcs} className="mt-3 w-full rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white gap-2" data-testid="bdoc-ics-save"><CalendarClock className="h-4 w-4" /> Save & Add to Calendar (.ics)</Button>
              <p className="mt-2 text-[11px] text-brand-navy/45">The .ics file adds the appointment to any calendar (Google, Apple, Outlook) with reminders 1 day and 1 hour before.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={userDialog.open} onOpenChange={(v) => setUserDialog((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy">Add Administrator</DialogTitle>
            <DialogDescription className="sr-only">Create a new administrator account for the website.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div><Label>Full name</Label><Input value={userDialog.name} onChange={(e) => setUserDialog((d) => ({ ...d, name: e.target.value }))} className="mt-1.5" data-testid="user-name" /></div>
            <div><Label>Email</Label><Input type="email" value={userDialog.email} onChange={(e) => setUserDialog((d) => ({ ...d, email: e.target.value }))} className="mt-1.5" data-testid="user-email" /></div>
            <div><Label>Temporary password</Label><Input type="text" value={userDialog.password} onChange={(e) => setUserDialog((d) => ({ ...d, password: e.target.value }))} className="mt-1.5" placeholder="Min 8 characters" data-testid="user-password" /></div>
          </div>
          <DialogFooter>
            <Button onClick={addAdmin} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white w-full" data-testid="user-save">Create Administrator</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shipMethodDialog.open} onOpenChange={(v) => setShipMethodDialog((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-md" data-testid="shipping-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy">{shipMethodDialog.editing ? "Edit" : "New"} Shipping Method</DialogTitle>
            <DialogDescription className="sr-only">Configure a shipping option shown to customers at checkout.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Method name</Label><Input value={shipMethodDialog.form.name} onChange={setShipField("name")} className="mt-1.5" placeholder="e.g. USPS Priority Mail" data-testid="sf-name" /></div>
            <div><Label>Carrier</Label><Input value={shipMethodDialog.form.carrier} onChange={setShipField("carrier")} className="mt-1.5" placeholder="USPS, UPS, FedEx…" data-testid="sf-carrier" /></div>
            <div><Label>Price ($)</Label><Input type="number" step="0.01" value={shipMethodDialog.form.price} onChange={setShipField("price")} className="mt-1.5" data-testid="sf-price" /></div>
            <div><Label>Free over ($)</Label><Input type="number" step="0.01" value={shipMethodDialog.form.free_over} onChange={setShipField("free_over")} className="mt-1.5" placeholder="Blank = never" data-testid="sf-freeover" /></div>
            <div><Label>Sort order</Label><Input type="number" value={shipMethodDialog.form.sort_order} onChange={setShipField("sort_order")} className="mt-1.5" data-testid="sf-sort" /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Input value={shipMethodDialog.form.description} onChange={setShipField("description")} className="mt-1.5" placeholder="e.g. 1–3 business days, tracked" data-testid="sf-desc" /></div>
            <label className="sm:col-span-2 flex items-center gap-3">
              <Switch checked={shipMethodDialog.form.enabled} onCheckedChange={(v) => setShipMethodDialog((d) => ({ ...d, form: { ...d.form, enabled: v } }))} data-testid="sf-enabled" />
              <span className="text-sm text-brand-navy">Visible to customers at checkout</span>
            </label>
          </div>
          <DialogFooter>
            <Button onClick={saveShipMethod} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white w-full" data-testid="sf-save">{shipMethodDialog.editing ? "Update" : "Create"} Method</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={winbackDialog.open} onOpenChange={(v) => setWinbackDialog((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-md" data-testid="winback-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy">Send a win-back discount</DialogTitle>
            <DialogDescription>{winbackDialog.cart ? `A unique single-use code will be emailed to ${winbackDialog.cart.email}.` : ""}</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Discount percentage</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input type="number" min="1" max="100" value={winbackDialog.percent} onChange={(e) => setWinbackDialog((d) => ({ ...d, percent: e.target.value }))} data-testid="winback-percent" />
              <span className="text-brand-navy/60">%</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitWinback} disabled={winbackDialog.sending} className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white w-full gap-2" data-testid="winback-submit">
              <Ticket className="h-4 w-4" /> {winbackDialog.sending ? "Sending…" : "Generate Code & Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={promoDialog.open} onOpenChange={(v) => setPromoDialog((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-md" data-testid="client-promo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy">Send a promo code</DialogTitle>
            <DialogDescription>{promoDialog.client ? `A unique single-use code will be emailed to ${promoDialog.client.email}.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Discount percentage</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input type="number" min="1" max="100" value={promoDialog.percent} onChange={(e) => setPromoDialog((d) => ({ ...d, percent: e.target.value }))} data-testid="promo-percent" />
                <span className="text-brand-navy/60">%</span>
              </div>
            </div>
            <div>
              <Label>Personal note (optional)</Label>
              <Textarea rows={3} value={promoDialog.note} onChange={(e) => setPromoDialog((d) => ({ ...d, note: e.target.value }))} className="mt-1.5" placeholder="e.g. Thanks for being a loyal customer — here's a treat!" data-testid="promo-note" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendPromo} disabled={promoDialog.sending} className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white w-full gap-2" data-testid="promo-send">
              <Gift className="h-4 w-4" /> {promoDialog.sending ? "Sending…" : "Generate Code & Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={pointsDialog.open} onOpenChange={(v) => setPointsDialog((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-md" data-testid="points-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy">Adjust rewards credit</DialogTitle>
            <DialogDescription>{pointsDialog.client ? `${pointsDialog.client.email} currently has $${Number(pointsDialog.client.reward_balance || 0).toFixed(2)}. Use a negative number to deduct.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Amount to add / remove ($)</Label><Input type="number" step="0.01" value={pointsDialog.amount} onChange={(e) => setPointsDialog((d) => ({ ...d, amount: e.target.value }))} className="mt-1.5" placeholder="e.g. 10 or -5" data-testid="points-amount" /></div>
            <div><Label>Note (optional)</Label><Input value={pointsDialog.note} onChange={(e) => setPointsDialog((d) => ({ ...d, note: e.target.value }))} className="mt-1.5" placeholder="Reason for the adjustment" data-testid="points-note" /></div>
          </div>
          <DialogFooter>
            <Button onClick={savePoints} disabled={pointsDialog.saving} className="rounded-full bg-brand-navy hover:bg-brand-blue text-white w-full gap-2" data-testid="points-save">
              <Star className="h-4 w-4" /> {pointsDialog.saving ? "Saving…" : "Update Balance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-brand-navy/10 bg-white p-5">
    <div className="flex items-center justify-between">
      <p className="font-mono-label text-[10px] uppercase tracking-[0.15em] text-brand-navy/45">{label}</p>
      <Icon className="h-4 w-4 text-brand-blue" />
    </div>
    <p className="mt-2 font-display text-3xl text-brand-navy">{value}</p>
  </div>
);

const Empty = ({ text }) => (
  <div className="rounded-xl border border-brand-navy/10 bg-brand-mist p-10 text-center text-brand-navy/55">{text}</div>
);
